import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Send, Trash2, Wand2, Loader2, Save, Check,
  MapPin, Users, Sword, BookOpen, Skull, Church, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_TYPE_META = {
  npc: { label: "NPC", icon: Users, color: "text-emerald-400", bg: "bg-emerald-400/10 border-emerald-400/30" },
  location: { label: "Location", icon: MapPin, color: "text-blue-400", bg: "bg-blue-400/10 border-blue-400/30" },
  building: { label: "Building", icon: Building2, color: "text-amber-400", bg: "bg-amber-400/10 border-amber-400/30" },
  item: { label: "Magic Item", icon: Sword, color: "text-purple-400", bg: "bg-purple-400/10 border-purple-400/30" },
  creature: { label: "Creature", icon: Skull, color: "text-red-400", bg: "bg-red-400/10 border-red-400/30" },
  monster: { label: "Creature", icon: Skull, color: "text-red-400", bg: "bg-red-400/10 border-red-400/30" },
  lore: { label: "Lore Entry", icon: BookOpen, color: "text-cyan-400", bg: "bg-cyan-400/10 border-cyan-400/30" },
  deity: { label: "Deity", icon: Church, color: "text-yellow-400", bg: "bg-yellow-400/10 border-yellow-400/30" },
};

const QUICK_PROMPTS = [
  { label: "Random NPC", prompt: "Generate a detailed D&D NPC with name, race, class, personality, and a secret. Make them interesting and memorable." },
  { label: "Quest Hook", prompt: "Create a unique quest hook for a D&D party. Include the quest giver, objective, potential rewards, and a twist." },
  { label: "Magic Item", prompt: "Invent a creative magic item for D&D 5e. Include its name, rarity, description, and mechanical properties." },
  { label: "Tavern", prompt: "Describe a unique tavern with its name, atmosphere, specialty drink, notable patrons, and a rumor the party might overhear." },
  { label: "Plot Twist", prompt: "Suggest a dramatic plot twist for a D&D campaign that would surprise the players but feel earned." },
  { label: "Monster", prompt: "Create an unusual monster encounter. Describe the creature, its lair, tactics, and what loot it might have." },
];

function parseEntityBlocks(text) {
  if (!text) return { cleanText: text, entities: [] };
  const entityRegex = /:::entity\s*\n?([\s\S]*?)\n?\s*:::/g;
  const entities = [];
  let match;
  while ((match = entityRegex.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed.type && parsed.data) entities.push(parsed);
    } catch { /* skip malformed */ }
  }
  const cleanText = text.replace(/:::entity\s*\n?[\s\S]*?\n?\s*:::/g, "").trim();
  return { cleanText, entities };
}

const ENTITY_NAV_MAP = {
  npc: "npcs",
  location: "locations",
  creature: "creatures",
  monster: "creatures",
  item: "items",
  lore: "lore",
  deity: "deities",
};

function SaveEntityButton({ entity, campaignId, locations, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const navigate = useNavigate();
  const meta = ENTITY_TYPE_META[entity.type] || ENTITY_TYPE_META.npc;
  const Icon = meta.icon;
  const isBuilding = entity.type === "building";

  const handleSave = async (locationId) => {
    setSaving(true);
    try {
      const entityData = { ...entity.data };
      if (isBuilding && locationId) {
        entityData.location_id = locationId;
      }
      const res = await axios.post(`${API}/ai/save-entity`, {
        campaign_id: campaignId,
        entity_type: entity.type,
        entity_data: entityData,
      }, { withCredentials: true });
      setSaved(true);
      setShowLocationPicker(false);
      const navTarget = ENTITY_NAV_MAP[entity.type];
      if (navTarget) {
        toast.success(res.data.message, {
          action: {
            label: `View in ${meta.label}s`,
            onClick: () => navigate(`/campaign/${campaignId}/${navTarget}`),
          },
        });
      } else {
        toast.success(res.data.message);
      }
      onSaved?.(entity);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save entity");
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => {
    if (isBuilding && !saved) {
      setShowLocationPicker(!showLocationPicker);
    } else {
      handleSave();
    }
  };

  return (
    <div className="inline-flex flex-col gap-1">
      <button
        onClick={handleClick}
        disabled={saving || saved}
        className={`flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-medium transition-all ${
          saved
            ? "bg-emerald-400/10 border-emerald-400/40 text-emerald-400 cursor-default"
            : `${meta.bg} ${meta.color} hover:brightness-125 cursor-pointer`
        }`}
        data-testid={`save-entity-${entity.type}-${entity.data?.name?.toLowerCase().replace(/\s/g, '-') || 'unknown'}`}
      >
        {saved ? <Check className="w-3 h-3" /> : saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
        {saved ? `Saved ${meta.label}` : `Save as ${meta.label}`}
        {entity.data?.name && <span className="opacity-70">— {entity.data.name}</span>}
      </button>
      {isBuilding && showLocationPicker && !saved && (
        <div className="flex items-center gap-1.5 animate-in fade-in" data-testid="building-location-picker">
          <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
            <SelectTrigger className="h-7 text-xs w-44 bg-card border-border">
              <SelectValue placeholder="Link to location..." />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="none">No location</SelectItem>
              {(locations || []).map(l => (
                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => handleSave(selectedLocationId === "none" ? "" : selectedLocationId)}
            disabled={saving}
            className="px-2 py-1 rounded text-xs bg-amber-400/20 border border-amber-400/40 text-amber-400 hover:brightness-125"
            data-testid="building-save-confirm"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
        </div>
      )}
    </div>
  );
}

const AIOraclePage = () => {
  const { campaignId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [locations, setLocations] = useState([]);
  const confirm = useConfirm();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchHistory(); fetchLocations(); }, [campaignId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true });
      setLocations(res.data);
    } catch { /* no locations */ }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/ai/history`, { withCredentials: true });
      const hist = res.data.map(h => ([
        { role: "user", content: h.message, timestamp: h.created_at },
        { role: "oracle", content: h.response, timestamp: h.created_at }
      ])).flat();
      setMessages(hist);
    } catch { /* no history */ }
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        campaign_id: campaignId, message: msg, session_id: sessionId
      }, { withCredentials: true });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "oracle", content: res.data.response }]);
    } catch (error) {
      if (error.response?.status === 403) {
        setMessages(prev => [...prev, { role: "oracle", content: "*The Oracle requires a higher calling...* Upgrade to Adventurer or Archmage to access the AI Oracle." }]);
        toast.error(error.response.data.detail, {
          action: { label: "Upgrade", onClick: () => window.location.href = "/subscription" }
        });
      } else {
        const detail = error.response?.data?.detail || "The Oracle is unavailable";
        setMessages(prev => [...prev, { role: "oracle", content: `*The Oracle's crystal ball flickers...* ${detail}` }]);
        toast.error("AI request failed");
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = async () => {
    const ok = await confirm("Clear History", "Clear all Oracle conversation history for this campaign?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/campaigns/${campaignId}/ai/history`, { withCredentials: true });
      setMessages([]);
      setSessionId(null);
      toast.success("History cleared");
    } catch {
      toast.error("Failed to clear history");
    }
  };

  const renderMarkdown = (text) => {
    let html = text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^### (.+)$/gm, '<h4 class="text-sm font-bold text-primary mt-2 mb-1">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 class="text-base font-bold text-primary mt-2 mb-1">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="text-lg font-bold text-primary mt-2 mb-1">$1</h2>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
      .replace(/\n\n/g, '<br/><br/>');
    return html;
  };

  const renderOracleMessage = (msg, idx) => {
    const { cleanText, entities } = parseEntityBlocks(msg.content);
    return (
      <div key={idx} className="flex justify-start">
        <div className="max-w-[80%]" data-testid="chat-message-oracle">
          <div className="px-4 py-3 rounded-sm bg-muted/20 border border-border text-foreground">
            <div className="text-sm prose-sm" dangerouslySetInnerHTML={{ __html: renderMarkdown(cleanText) }} />
          </div>
          {entities.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="entity-save-buttons">
              <Save className="w-3.5 h-3.5 text-muted-foreground mt-1" />
              {entities.map((ent, i) => (
                <SaveEntityButton key={i} entity={ent} campaignId={campaignId} locations={locations} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout tabLabel="AI Oracle">      <div className="flex flex-col h-[calc(100vh-8rem)]" data-testid="ai-oracle-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">AI Oracle</h1>
            <p className="text-muted-foreground text-sm">Ask the Oracle for D&D ideas — save generated content directly to your campaign</p>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={clearHistory} data-testid="clear-history-button">
              <Trash2 className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        <TierGate feature="AI Oracle" description="Generate NPCs, quest hooks, lore, monsters, and more with AI — then save them directly to your campaign entities.">

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 scrollbar-thin" data-testid="chat-messages">
          {messages.length === 0 && !loading && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Wand2 className="w-16 h-16 text-primary/30 mb-4" />
              <h3 className="text-lg font-serif text-foreground mb-2">Consult the Oracle</h3>
              <p className="text-sm text-muted-foreground mb-2 max-w-md">
                Ask about D&D rules, generate NPCs, quest hooks, magic items, or get worldbuilding inspiration.
              </p>
              <p className="text-xs text-muted-foreground mb-6 max-w-sm opacity-70">
                Generated NPCs, locations, items and more can be saved directly to your campaign with one click.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {QUICK_PROMPTS.map((qp) => (
                  <Button
                    key={qp.label}
                    variant="outline"
                    size="sm"
                    className="border-border text-xs rounded-sm"
                    onClick={() => sendMessage(qp.prompt)}
                    data-testid={`quick-prompt-${qp.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <Wand2 className="w-3 h-3 mr-1" /> {qp.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) =>
            msg.role === "oracle"
              ? renderOracleMessage(msg, i)
              : (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[80%] px-4 py-3 rounded-sm bg-primary/20 text-foreground" data-testid="chat-message-user">
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              )
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-muted/20 border border-border px-4 py-3 rounded-sm">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="italic">The Oracle peers into the aether...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Prompts (when messages exist) */}
        {messages.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3 flex-shrink-0">
            {QUICK_PROMPTS.map((qp) => (
              <Button
                key={qp.label}
                variant="outline"
                size="sm"
                className="border-border text-[10px] h-6 px-2 rounded-sm"
                onClick={() => sendMessage(qp.prompt)}
                disabled={loading}
              >
                {qp.label}
              </Button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="flex gap-2 flex-shrink-0">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="Ask the Oracle anything about D&D..."
            className="bg-input border-border rounded-sm flex-1"
            disabled={loading}
            data-testid="oracle-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="bg-primary text-primary-foreground rounded-sm"
            data-testid="oracle-send-button"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        </TierGate>
      </div>
    </Layout>
  );
};

export default AIOraclePage;
